import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import baseURL from '../component/baseURL';
import showNotification from '../component/Notification';
import stringsoflanguages from '../component/stringsoflanguages';

const labels = ['Not Shared', 'B to B', 'B to C'];

function getSelectedTypeLabel(businessType) {
    if (String(businessType) === '1') return 'B to B';
    if (String(businessType) === '2') return 'B to C';
    return 'Not Shared';
}

function toNumber(v) {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
}

function isSubscribedChecked(v) {
    const s = String(v ?? '').trim();
    if (s === '0') return true;
    if (s === '1') return false;
    return false;
}

/** Backend returns pocket under feed5_* and "Feed 5" under feed6_*; map to UI block ids 5 / 6. */
function apiFieldIndexForBlockId(blockId) {
    const n = Number(blockId);
    if (n === 5) return 6;
    if (n === 6) return 5;
    return n;
}

export default function ConnectionFeedSettings({ hideMobileHeader = false }) {
    const location = useLocation();
    const navigate = useNavigate();
    const stateMemberId =
        location.state?.member_id ??
        location.state?.memberID ??
        location.state?.user_details?.user_id ??
        location.state?.member?.user_id ??
        '';

    const [isMobile, setIsMobile] = useState(false);
    const [loading, setLoading] = useState(true);
    const [memberDetails, setMemberDetails] = useState(location.state?.user_details ?? location.state?.member ?? {});
    const [feedBlocks, setFeedBlocks] = useState([]);
    const [leaveConfirmOpen, setLeaveConfirmOpen] = useState(false);
    const pendingNavigationRef = useRef(null);
    const allowPopRef = useRef(false);
    const shouldConfirmLeave = true;
    const updatedFeed = location.state?.updatedFeed;

    const fullName = useMemo(
        () => (memberDetails?.user_name || '').trim() || [memberDetails?.fname, memberDetails?.lname].filter(Boolean).join(' ') || 'Member',
        [memberDetails]
    );
    const memberLocation = useMemo(
        () => [memberDetails?.city_name, memberDetails?.country_name].filter(Boolean).join(' - ') || '—',
        [memberDetails]
    );
    const memberImage = memberDetails?.user_image_thumb || memberDetails?.user_image || 'https://placehold.co/80x80';
    const companyImage = memberDetails?.company_image_thumb || memberDetails?.company_image || 'https://placehold.co/100x50?text=Logo';
    const totalPublished = toNumber(memberDetails?.total_published);
    const totalAccepted = toNumber(memberDetails?.total_accepted);
    const totalShared = toNumber(memberDetails?.total_shared);
    const resolvedMemberId =
        stateMemberId ||
        localStorage.getItem('connection_feed_member_id') ||
        localStorage.getItem('member_id') ||
        '';

    function buildConnectionFeedSettingsPageState(overrides = {}) {
        return {
            member_id: memberDetails?.user_id || resolvedMemberId || '',
            memberID: memberDetails?.user_id || resolvedMemberId || '',
            user_details: memberDetails,
            member: memberDetails,
            returnTo: location.state?.returnTo,
            ...overrides,
        };
    }

    function handleConnectionFeedSettingsBack() {
        const returnTo = location.state?.returnTo;
        if (returnTo?.path) {
            navigate(returnTo.path, { replace: true, state: returnTo.state ?? {} });
            return;
        }
        if (location.state?.from) {
            navigate(location.state.from, { replace: true });
            return;
        }
        navigate('/feed', { replace: true });
    }

    function navigateToMemberProfile() {
        const memberId = memberDetails?.user_id || resolvedMemberId || '';
        navigate('/member-profile', {
            replace: true,
            state: {
                memberID: memberId,
                member_id: memberId,
                member: memberDetails,
                from: '/feed/connection-feed-settings',
                returnTo: {
                    path: '/feed/connection-feed-settings',
                    state: buildConnectionFeedSettingsPageState(),
                },
            },
        });
    }

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 767);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (stateMemberId) {
            localStorage.setItem('connection_feed_member_id', stateMemberId);
        }
    }, [stateMemberId]);

    useEffect(() => {
        const userId = localStorage.getItem('user_id');
        if (!userId || !resolvedMemberId) {
            setLoading(false);
            return;
        }
        fetchDetails(userId, resolvedMemberId);
    }, [resolvedMemberId]);

    useEffect(() => {
        if (!updatedFeed) return;
        setFeedBlocks((prev) =>
            prev.map((feed) => {
                if (Number(feed.id) !== Number(updatedFeed.feedType)) return feed;
                return {
                    ...feed,
                    selectedIndex: Number(updatedFeed.shareType ?? feed.selectedIndex ?? 0),
                    selectedType: updatedFeed.selectedType ?? feed.selectedType,
                    listingIds: String(updatedFeed.listingIds ?? feed.listingIds ?? ''),
                };
            })
        );
    }, [updatedFeed]);

    useEffect(() => {
        if (!shouldConfirmLeave) return undefined;
        // Keep one history state in front so browser back can be confirmed.
        window.history.pushState({ feedShareGuard: true }, '');
        const handlePopState = () => {
            if (allowPopRef.current) {
                allowPopRef.current = false;
                return;
            }
            openLeaveConfirm(() => handleConnectionFeedSettingsBack());
            window.history.pushState({ feedShareGuard: true }, '');
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [shouldConfirmLeave]);

    function openLeaveConfirm(action) {
        pendingNavigationRef.current = action;
        setLeaveConfirmOpen(true);
    }

    function handleCancelLeave() {
        setLeaveConfirmOpen(false);
        const action = pendingNavigationRef.current;
        pendingNavigationRef.current = null;
        allowPopRef.current = true;
        if (typeof action === 'function') {
            action();
            return;
        }
        handleConnectionFeedSettingsBack();
    }

    function toSubscribedApiValue(checked) {
        return checked ? '0' : '1';
    }

    function handleSubscribedChange(feedId, checked) {
        setFeedBlocks((prev) =>
            prev.map((feed) =>
                Number(feed.id) === Number(feedId)
                    ? { ...feed, subscribed: checked }
                    : feed
            )
        );
    }

    function getFeedById(feedId) {
        return feedBlocks.find((f) => Number(f.id) === Number(feedId));
    }

    function normalizeListingIdsByShareType(listingIds, shareType) {
        if (Number(shareType) === 0) return '';
        return String(listingIds || '').replace(/,\s*$/, '');
    }

    async function retrieveUpdatedData() {
        const userId = localStorage.getItem('user_id');
        const memberId = memberDetails?.user_id || resolvedMemberId || '';
        if (!userId || !memberId) {
            showNotification('Error', 'Missing user/member info.', 'danger');
            return false;
        }

        const formdata = new FormData();
        formdata.append('user_id', userId);
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        formdata.append('member_id', memberId);

        [1, 2, 3, 4, 5, 6].forEach((id) => {
            const feed = getFeedById(id);
            const selectedIndex = Number(feed?.selectedIndex ?? 0);
            const listingIds = normalizeListingIdsByShareType(feed?.listingIds ?? '', selectedIndex);
            const subscribed = toSubscribedApiValue(Boolean(feed?.subscribed));
            const apiIdx = apiFieldIndexForBlockId(id);
            formdata.append(`feed${apiIdx}_listing_ids`, listingIds);
            formdata.append(`feed${apiIdx}_sharing_type`, String(selectedIndex));
            formdata.append(`feed${apiIdx}_subscribed`, subscribed);
        });

        try {
            const response = await fetch(baseURL.URL + '/portfolio_feed/feed_share', {
                method: 'POST',
                body: formdata,
            });
            const responseJson = await response.json();
            if (responseJson.server?.code === 200) {
                showNotification('Success', responseJson.server?.message || 'Feed shared successfully.', 'success');
                return true;
            }
            showNotification('Error', responseJson.server?.message || 'Failed to share feed.', 'danger');
            return false;
        } catch (error) {
            showNotification('Error', error.message || 'Failed to share feed.', 'danger');
            return false;
        }
    }

    async function handleConfirmLeave() {
        const action = pendingNavigationRef.current;
        const ok = await retrieveUpdatedData();
        if (!ok) return;
        pendingNavigationRef.current = null;
        setLeaveConfirmOpen(false);
        allowPopRef.current = true;
        if (typeof action === 'function') action();
    }

    function buildFeedBlocksFromResult(result) {
        return [1, 2, 3, 4, 5, 6].map((blockId) => {
            const apiIdx = apiFieldIndexForBlockId(blockId);
            const businessType = result[`feed${apiIdx}_business_type`];
            const subscribed = isSubscribedChecked(result[`feed${apiIdx}_subscribed`]);
            const count = result[`feed${apiIdx}_count`] ?? {};
            const selectedIndex = String(businessType).trim() === '1' ? 1 : String(businessType).trim() === '2' ? 2 : 0;

            return {
                id: blockId,
                title: blockId === 6 ? 'Pocket Feed' : `Feed ${blockId}`,
                listingIds: result[`feed${apiIdx}_listing_ids`] ?? '',
                selectedType: getSelectedTypeLabel(businessType),
                selectedIndex,
                subscribed,
                totalPublished: toNumber(count.total_published),
                totalShared: toNumber(count.total_shared),
                totalAccepted: toNumber(count.total_accepted),
            };
        });
    }

    function fetchDetails(userId, memberId) {
        setLoading(true);
        const formdata = new FormData();
        formdata.append('user_id', userId);
        formdata.append('key', '81dc9bdb52d04dc20036dbd8313ed055');
        formdata.append('member_id', memberId);

        fetch(baseURL.URL + '/portfolio_feed/fetch_feed_shared', {
            method: 'POST',
            body: formdata,
        })
            .then((response) => response.json())
            .then((responseJson) => {
                if (responseJson.server?.code === 200) {
                    const result = responseJson.response?.result ?? {};
                    setMemberDetails(result.member_details ?? {});
                    let nextBlocks = buildFeedBlocksFromResult(result);
                    if (updatedFeed) {
                        nextBlocks = nextBlocks.map((feed) => {
                            if (Number(feed.id) !== Number(updatedFeed.feedType)) return feed;
                            return {
                                ...feed,
                                selectedIndex: Number(updatedFeed.shareType ?? feed.selectedIndex ?? 0),
                                selectedType: updatedFeed.selectedType ?? feed.selectedType,
                                listingIds: String(updatedFeed.listingIds ?? feed.listingIds ?? ''),
                            };
                        });
                    }
                    setFeedBlocks(nextBlocks);
                } else {
                    showNotification('Error', responseJson.server?.message || 'Failed to load feed settings', 'danger');
                }
            })
            .catch((error) => {
                showNotification('Error', error.message || 'Failed to load feed settings', 'danger');
            })
            .finally(() => setLoading(false));
    }

    return (
        <React.Fragment>
            {!hideMobileHeader && isMobile && (
                <div className="mobile-page-header incoming-member-feed-header connection-feed-settings-mobile-header flex align-items-center">
                    <button
                        type="button"
                        className="mobile-back-btn"
                        onClick={() => openLeaveConfirm(() => handleConnectionFeedSettingsBack())}
                        aria-label="Back"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </button>
                    <h2 className="mobile-page-title incoming-member-feed-title">{stringsoflanguages.connectionFeedSettings}</h2>
                </div>
            )}

            <div className="content-right connection-feed-settings-page">


                <div className="connection-feed-settings-wrap">
                    {loading ? (
                        <div className="page-loader-wrapper" style={{ padding: 16 }}>
                            <div className="react-js-loader">
                                <div className="react-js-loader-spinner"></div>
                                <p className="react-js-loader-text">{stringsoflanguages.loading}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="connection-feed-settings-header flex gap-4 w-full mb-4">
                                <div className="connection-feed-member-card w-full cursor-pointer" onClick={() => {
                                    openLeaveConfirm(() => navigateToMemberProfile());
                                }}>
                                    <div className="connection-feed-member-left">
                                        <img src={memberImage} alt={fullName} className="connection-feed-member-avatar" />
                                        <div className="connection-feed-member-meta">
                                            <h3>{fullName}</h3>
                                            <p>{memberLocation}</p>
                                        </div>
                                    </div>
                                    <img src={companyImage} alt="Company" className="connection-feed-company-logo" />
                                </div>

                                <div className="connection-feed-stat-box w-full">
                                    <div className="connection-feed-stat-item">
                                        <strong>{totalPublished}</strong>
                                        <span>{stringsoflanguages.totalPublished}</span>
                                    </div>
                                    <div className="connection-feed-stat-divider" />
                                    <div className="connection-feed-stat-item">
                                        <strong>{totalAccepted}</strong>
                                        <span>{stringsoflanguages.totalAccepted}</span>
                                    </div>
                                    <div className="connection-feed-stat-divider" />
                                    <div className="connection-feed-stat-item">
                                        <strong>{totalShared}</strong>
                                        <span>{stringsoflanguages.totalShared}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="connection-feed-settings-content">
                                {feedBlocks.map((feed) => (

                                    <div key={feed.id} className="connection-feed-block">
                                        <div className="connection-feed-tab-row">
                                            {labels.map((label) => (
                                                <span
                                                    key={label}
                                                    className={`connection-feed-tab ${feed.selectedType === label ? 'active' : ''}`}
                                                >
                                                    {label}
                                                </span>
                                            ))}
                                        </div>
                                        <div className="connection-feed-content-row">
                                            <div
                                                className="connection-feed-name-box"
                                                onClick={() =>
                                                    navigate('/feed/connection-feed-settings-details', {
                                                        state: {
                                                            feedType: feed.id,
                                                            apiFeedType: apiFieldIndexForBlockId(feed.id),
                                                            shareType: feed.selectedIndex,
                                                            listingIds: feed.listingIds ?? '',
                                                            memberId: memberDetails?.user_id ?? resolvedMemberId,
                                                            memberDetails,
                                                            totalPublished,
                                                            totalAccepted,
                                                            totalShared,
                                                            feedTitle: feed.title,
                                                            selectedType: feed.selectedType,
                                                            returnTo: {
                                                                path: '/feed/connection-feed-settings',
                                                                state: buildConnectionFeedSettingsPageState(),
                                                            },
                                                        },
                                                    })
                                                }
                                            >
                                                {feed.title}
                                            </div>
                                            <label className="connection-feed-subscribed">
                                                <span>{stringsoflanguages.subscribed}</span>
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(feed.subscribed)}
                                                    onChange={(e) => handleSubscribedChange(feed.id, e.target.checked)}
                                                />
                                            </label>
                                        </div>
                                        <div className="connection-feed-count-row">
                                            <div className="connection-feed-count-box">
                                                <span>{stringsoflanguages.total}</span>
                                                <strong>{feed.totalPublished}</strong>
                                            </div>
                                            <div className="connection-feed-count-box">
                                                <span>{stringsoflanguages.shared}</span>
                                                <strong>{feed.totalShared}</strong>
                                            </div>
                                            <div className="connection-feed-count-box">
                                                <span>{stringsoflanguages.accepted}</span>
                                                <strong>{feed.totalAccepted}</strong>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {leaveConfirmOpen && (
                <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Share feed confirmation">
                    <div className="logout-modal">
                        <h2 className='text-white mb-2'>{stringsoflanguages.confirmation}</h2>
                        <p className='text-white mb-4'>{stringsoflanguages.areYouSureYouWantToShareYourFeed}</p>
                        <div className="btn-sec">
                            <button type="button" className="save-btn" onClick={handleConfirmLeave}>{stringsoflanguages.yes}</button>
                            <button type="button" className="cancel-btn" onClick={handleCancelLeave}>{stringsoflanguages.no}</button>
                        </div>
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}
